/**
 * A table of contents that is the document's actual shape, not a copy of it.
 *
 * NOTHING IS STORED BUT THE FACT THAT IT IS THERE. The node has no content: the entries are drawn
 * from the outline every time, at both ends. A stored list of headings is a second copy of the
 * document's structure, and the first time somebody renames a section the two disagree — in a
 * printed procedure, with nothing saying which is right.
 *
 * WHICH IS WHY IT NEEDS NO "UPDATE" BUTTON. Every word processor that stores its contents list has
 * one, and every one of them has readers who never press it.
 *
 * AN EMPTY DOCUMENT GETS A LINE SAYING SO rather than an invisible node. A block that renders to
 * nothing reads as a bug, and somebody will delete it and insert it again.
 */
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { outlineOf } from "./outline";

export const EMPTY = "No headings yet — a contents list fills itself in as you add them.";

export const TableOfContents = Node.create({
    name: "tableOfContents",
    group: "block",
    atom: true,
    // NOT DRAGGABLE AND NOT SELECTABLE AS CONTENT: it is furniture, and the only edits that make
    // sense are inserting it and deleting it.
    selectable: true,

    parseHTML() {
        return [{ tag: "div[data-toc]" }];
    },

    renderHTML({ HTMLAttributes }) {
        // EMPTY HERE, FILLED BY THE PASS BELOW -- renderHTML knows nothing about the rest of the
        // document, and a contents list is entirely about the rest of the document.
        return ["div", mergeAttributes(HTMLAttributes, { "data-toc": "", class: "parkour-toc" })];
    },
});

const key = new PluginKey("tableOfContentsText");

/**
 * Draw every contents list, as a NODE VIEW that owns its own DOM.
 *
 * NOT BY WRITING INTO THE EDITOR'S DOM. The first version rebuilt the list's children on every
 * update, which is mutating ProseMirror's DOM behind its back: the observer sees it, treats it as
 * typing, dispatches a transaction, and that triggers the next rebuild. The editor spent its time
 * in a loop and stopped answering clicks.
 *
 * A node view with `ignoreMutation` is the supported way to own a piece of the editor's DOM. It
 * says to ProseMirror: this subtree is mine, do not read it back as content. And because a
 * contents list is about the WHOLE document, it redraws on every transaction rather than only when
 * its own node changes -- renaming a section three pages away is exactly the edit it has to notice.
 */
export const TableOfContentsText = Extension.create({
    name: "tableOfContentsText",

    addProseMirrorPlugins() {
        // The views alive right now, so a transaction anywhere can redraw all of them.
        const views = new Set<{ redraw: (doc: any) => void }>();

        return [new Plugin({
            key,
            view: () => ({
                update: (view) => views.forEach(one => one.redraw(view.state.doc)),
            }),
            props: {
                nodeViews: {
                    tableOfContents: (_node, view) => {
                        const dom = document.createElement("div");
                        dom.className = "parkour-toc";
                        dom.setAttribute("data-toc", "");
                        // NOT EDITABLE. Its content is computed; letting somebody type into it
                        // would create the stale copy this design exists to avoid.
                        dom.contentEditable = "false";

                        const redraw = (doc: any) => {
                            const entries = outlineOf(doc.toJSON(),
                                                      Boolean(doc.attrs?.outlineNumbering));
                            dom.textContent = "";
                            if (!entries.length) {
                                const empty = document.createElement("div");
                                empty.className = "parkour-toc-empty";
                                empty.textContent = EMPTY;
                                dom.appendChild(empty);
                                return;
                            }
                            entries.forEach((entry) => {
                                const row = document.createElement("div");
                                row.className = "parkour-toc-entry";
                                row.style.paddingLeft = `${(entry.level - 1) * 1.2}em`;
                                row.textContent = entry.number
                                    ? `${entry.number}\u00a0\u00a0${entry.text}`
                                    : entry.text;
                                dom.appendChild(row);
                            });
                        };

                        const handle = { redraw };
                        views.add(handle);
                        redraw(view.state.doc);

                        return {
                            dom,
                            // THE WHOLE POINT: this subtree is the node view's, and ProseMirror
                            // must not read it back as document content.
                            ignoreMutation: () => true,
                            update: () => true,
                            destroy: () => { views.delete(handle); },
                        };
                    },
                },
            },
        })];
    },
});
